import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Mic, Square, Volume2, VolumeX, Loader2, AudioLines, History } from "lucide-react";
import { useChatty } from "./ChattyContext";
import { ReasoningStream, ORCHESTRA_CADENCE } from "../components/ReasoningStream";

const SUGGESTIONS = [
  "What services does Nexis offer?",
  "How do I use the NexSys dashboard?",
  "Which industries do you serve?",
  "How can I book a consultation?",
];

const NEXSYS_SUGGESTIONS = [
  "What are the top risks right now?",
  "Brief me on portfolio health",
  "Open Cognitive Core",
  "What changed in risk this week?",
];

const VIEWER_TOUR = [
  "What is the current state of the enterprise?",
  "What decisions are currently active, and what evidence supports them?",
  "What has changed that could affect an existing decision?",
  "What risks or unresolved conditions should leadership be aware of?",
  "If I came back tomorrow, what context would NexSys preserve?",
];

// tiny, safe markdown-ish renderer (bold + line breaks + bullets)
function render(text) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^- (.*)$/gm, "• $1")
    .replace(/\n/g, "<br/>");
}

export default function Chatty() {
  const {
    open, setOpen, busy, messages, sendMessage,
    isNexsys, voiceEnabled, setVoiceEnabled, speaking, stopSpeaking, askByVoice, transcribing,
    conversation, setConversation, listenSignal, unlockAudio, briefing,
    orchestrating, openDecision, resumed,
  } = useChatty();
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [micError, setMicError] = useState("");
  const scrollRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const vadRafRef = useRef(null);
  const maxTimerRef = useRef(null);
  const speechRef = useRef(false);
  const silenceStartRef = useRef(null);
  const shouldSendRef = useRef(true);
  const conversationRef = useRef(conversation);
  useEffect(() => { conversationRef.current = conversation; }, [conversation]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, open, transcribing, briefing, orchestrating]);

  const submit = (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    unlockAudio?.();
    sendMessage(text.trim());
    setText("");
  };

  const cleanupVad = useCallback(() => {
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    vadRafRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
  }, []);

  const stopRec = useCallback(() => {
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
    try { recRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  }, []);

  // Voice-activity detection: calibrates to ambient noise, then auto-stops (and sends)
  // shortly after you stop talking — fully hands-free, no button press.
  const setupVad = useCallback((stream) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      ctx.resume?.().catch(() => {});
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const startedAt = Date.now();
      speechRef.current = false;
      silenceStartRef.current = null;
      let calibrated = false, calibSum = 0, calibN = 0, floor = 0.008, speechFrames = 0;
      const SILENCE_MS = 600, NOSPEECH_MS = 8000, MAX_MS = 20000, CALIB_MS = 280;
      const tick = () => {
        if (!audioCtxRef.current) return;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        const elapsed = now - startedAt;
        if (!calibrated) {
          calibSum += rms; calibN += 1;
          if (elapsed >= CALIB_MS) { floor = calibSum / Math.max(calibN, 1); calibrated = true; }
          vadRafRef.current = requestAnimationFrame(tick);
          return;
        }
        const speakTh = Math.max(floor * 2.4, 0.011);
        const silenceTh = Math.max(floor * 1.5, 0.008);
        if (rms > speakTh) {
          speechFrames += 1;
          if (speechFrames >= 2) speechRef.current = true; // ignore single-frame blips
          silenceStartRef.current = null;
        } else if (speechRef.current && rms < silenceTh) {
          if (silenceStartRef.current == null) silenceStartRef.current = now;
          else if (now - silenceStartRef.current > SILENCE_MS) { shouldSendRef.current = true; stopRec(); return; }
        }
        if (!speechRef.current && elapsed > NOSPEECH_MS) { shouldSendRef.current = false; stopRec(); return; }
        if (elapsed > MAX_MS) { shouldSendRef.current = speechRef.current; stopRec(); return; }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch { /* VAD unsupported → falls back to manual stop */ }
  }, [stopRec]);

  const startRec = useCallback(async (opts = {}) => {
    const vad = !!opts.vad;
    unlockAudio?.(); // unlock audio playback within this gesture (mobile)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      shouldSendRef.current = true;
      speechRef.current = false;
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        cleanupVad();
        if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        // Send whenever we should (manual stop OR VAD detected speech); only skip on a no-speech timeout.
        if (blob.size > 800 && shouldSendRef.current) askByVoice(blob);
        else if (vad && conversationRef.current) setTimeout(() => { if (conversationRef.current) startRec({ vad: true }); }, 250);
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
      // Safety net: if VAD can't run (no AudioContext / rAF throttled), never leave the mic stuck.
      if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
      maxTimerRef.current = setTimeout(() => stopRec(), 16000);
      if (vad) setupVad(stream);
    } catch {
      // mic permission denied / unsupported — tell the user instead of failing silently
      setRecording(false);
      setMicError("Mic unavailable — allow microphone access in your browser, then tap the mic again.");
      setTimeout(() => setMicError(""), 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askByVoice, unlockAudio, cleanupVad, setupVad]);

  const toggleRec = () => (recording ? stopRec() : startRec({ vad: true }));

  // Hands-free: after Chatty finishes speaking, auto-listen (with VAD) for the next turn.
  useEffect(() => {
    if (listenSignal > 0 && conversation && open && !recording && !busy && !transcribing) {
      startRec({ vad: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenSignal]);

  const toggleConversation = () => {
    if (conversation) {
      setConversation(false);
      shouldSendRef.current = false;
      stopRec();
    } else {
      setConversation(true);
      setVoiceEnabled(true);
      setOpen(true);
      startRec({ vad: true }); // this click grants mic access; VAD handles the rest hands-free
    }
  };

  const toggleVoice = () => {
    unlockAudio?.();
    if (voiceEnabled) stopSpeaking();
    setVoiceEnabled((v) => !v);
  };

  const isViewer = localStorage.getItem("nexsys_gate_role") === "viewer";
  const suggestions = isNexsys ? (isViewer ? VIEWER_TOUR : NEXSYS_SUGGESTIONS) : SUGGESTIONS;

  // Don't render Chatty on the gated NexSys route until the visitor is authenticated.
  const [hasGate, setHasGate] = useState(!!localStorage.getItem("nexsys_gate_token"));
  useEffect(() => {
    const on = () => setHasGate(!!localStorage.getItem("nexsys_gate_token"));
    window.addEventListener("nexsys-gate-change", on);
    return () => window.removeEventListener("nexsys-gate-change", on);
  }, []);

  const statusLine = micError
    ? micError
    : recording
    ? "Listening…"
    : transcribing
      ? "Transcribing…"
      : orchestrating
        ? "Reasoning across agents…"
        : busy
          ? "Thinking…"
          : speaking
            ? "Speaking…"
            : conversation
              ? "● Conversation mode · listening between turns"
              : isNexsys ? "● NEXSYS · Computational Intelligence Platform" : "● Online · Nexis assistant";

  if (isNexsys && !hasGate) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[100]">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="mb-3 flex h-[min(560px,76vh)] w-[min(92vw,384px)] flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0a0d1a]/95 backdrop-blur-2xl shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
            data-testid="chatty-panel"
          >
            {/* header */}
            <div className="flex items-center gap-3 border-b border-white/10 bg-gradient-to-r from-cyan-500/15 to-indigo-500/15 px-4 py-3.5">
              <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500">
                <Sparkles className="h-5 w-5 text-black" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0a0d1a] bg-emerald-400" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-base font-semibold text-white">Chatty</div>
                <div className={`text-[11px] ${micError ? "text-amber-300" : "text-emerald-400"}`}>{statusLine}</div>
                {isNexsys && !recording && !transcribing && !busy && !orchestrating && !speaking && !conversation && (
                  <div className="font-mono text-[9px] tracking-wide text-white/35">Conversational surface of NEXSYS</div>
                )}
              </div>
              <div className="ml-auto flex items-center gap-1">
                {isNexsys && (
                  <button
                    onClick={toggleConversation}
                    className={`rounded-full p-2 transition-colors ${conversation ? "bg-cyan-400/20 text-cyan-300" : "text-white/40 hover:bg-white/10 hover:text-white/70"}`}
                    data-testid="chatty-conversation-toggle"
                    title={conversation ? "Conversation mode on — tap to stop" : "Start hands-free conversation"}
                    aria-label="Toggle hands-free conversation"
                  >
                    <AudioLines className="h-5 w-5" />
                  </button>
                )}
                {isNexsys && (
                  <button
                    onClick={toggleVoice}
                    className={`rounded-full p-2 transition-colors ${voiceEnabled ? "text-cyan-300 hover:bg-white/10" : "text-white/40 hover:bg-white/10"}`}
                    data-testid="chatty-voice-toggle"
                    title={voiceEnabled ? "Voice replies on" : "Voice replies off"}
                    aria-label="Toggle voice replies"
                  >
                    {voiceEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="rounded-full p-2 text-white/50 hover:bg-white/10 hover:text-white" data-testid="chatty-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* messages */}
            {/* Continuum: resumed governed context indicator (only when actually resumed from an authorized CSO) */}
            {isNexsys && resumed?.resumed && (
              <div
                className="flex items-center gap-2 border-b border-white/[0.06] bg-cyan-500/[0.07] px-4 py-1.5"
                data-testid="chatty-resumed-context"
                title={(resumed.context_lines || []).slice(0, 5).join("\n")}
              >
                <History className="h-3 w-3 shrink-0 text-cyan-300" />
                <span className="truncate font-mono text-[9.5px] tracking-wide text-cyan-300/90">
                  RESUMED CONTEXT · {resumed.objects} governed object{resumed.objects === 1 ? "" : "s"} · CSO v{resumed.cso?.version}
                  {" · "}
                  {Object.entries(resumed.by_class || {}).map(([k, v]) => `${v} ${k.toLowerCase()}`).join(", ")}
                </span>
              </div>
            )}

            <div ref={scrollRef} className="nx-scroll flex-1 space-y-3 overflow-y-auto p-4" data-testid="chatty-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="mr-2 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500">
                      <Sparkles className="h-3.5 w-3.5 text-black" />
                    </div>
                  )}
                  <div className="flex max-w-[80%] flex-col items-start gap-1.5">
                    <div
                      className={`rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                        m.role === "user"
                          ? "self-end rounded-br-md bg-cyan-500 text-black"
                          : "rounded-bl-md border border-white/10 bg-white/[0.04] text-white/85"
                      }`}
                      dangerouslySetInnerHTML={{ __html: render(m.content) }}
                    />
                    {m.grounding && m.grounding.length > 0 && (
                      <div className="font-mono text-[9.5px] tracking-wide text-white/35" data-testid="chatty-grounding">
                        grounded in: {m.grounding.join(" · ")}
                      </div>
                    )}
                    {m.decision && (
                      <button
                        onClick={() => openDecision(m.decision)}
                        className="flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3 py-1 text-[11px] font-medium text-cyan-200 transition-colors hover:bg-cyan-400/20"
                        data-testid="chatty-open-decision"
                      >
                        <Sparkles className="h-3 w-3" /> View full Decision Object →
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {(busy || transcribing || briefing || orchestrating) && (
                <div className="flex justify-start">
                  <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500">
                    <Sparkles className="h-3.5 w-3.5 text-black" />
                  </div>
                  <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.04] px-3.5 py-3">
                    {orchestrating ? (
                      <ReasoningStream preset="orchestra" compact spine cadence={ORCHESTRA_CADENCE} />
                    ) : briefing ? (
                      <ReasoningStream preset="briefing" compact />
                    ) : busy && isNexsys ? (
                      <ReasoningStream preset="portfolio" compact />
                    ) : transcribing ? (
                      <span className="inline-flex items-center gap-2 text-[12px] text-white/70">
                        <span className="inline-flex gap-1">
                          {[0, 1, 2].map((d) => (
                            <span key={d} className="h-1.5 w-1.5 rounded-full bg-cyan-300" style={{ animation: `nx-pulse 1.2s ${d * 0.2}s infinite` }} />
                          ))}
                        </span>
                        Transcribing…
                      </span>
                    ) : (
                      <span className="inline-flex gap-1">
                        {[0, 1, 2].map((d) => (
                          <span key={d} className="h-1.5 w-1.5 rounded-full bg-cyan-300" style={{ animation: `nx-pulse 1.2s ${d * 0.2}s infinite` }} />
                        ))}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* suggestions (only before user engages) */}
            {!messages.some((m) => m.role === "user") && (
              <div className="flex flex-wrap gap-1.5 px-4 pb-2">
                {isNexsys && isViewer && (
                  <div className="w-full" data-testid="chatty-guided-tour-label">
                    <div className="font-mono text-[9px] tracking-[0.25em] text-cyan-400/70">GUIDED DEMO · READ-ONLY</div>
                    <div className="mt-1 text-[12px] font-semibold text-white/85">Explore NexSys</div>
                    <div className="mb-1 text-[10px] text-white/45">See how governed enterprise context changes what AI can understand.</div>
                  </div>
                )}
                {suggestions.map((s) => (
                  <button key={s} onClick={() => sendMessage(s)} className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-white/60 transition-colors hover:border-cyan-400/40 hover:text-white" data-testid="chatty-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* input */}
            <form onSubmit={submit} className="flex items-center gap-2 border-t border-white/10 p-3">
              {isNexsys && (
                <button
                  type="button"
                  onClick={toggleRec}
                  disabled={busy || transcribing}
                  className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors disabled:opacity-50 ${
                    recording ? "border-transparent bg-pink-500 text-white" : "border-white/10 bg-white/[0.04] text-cyan-300 hover:border-cyan-400/50"
                  }`}
                  data-testid="chatty-mic"
                  title={recording ? "Stop & send" : "Speak to Chatty"}
                  aria-label="Push to talk"
                >
                  {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  {recording && <span className="absolute inset-0 -z-10 rounded-xl bg-pink-500/50 blur-md nx-pulse" />}
                </button>
              )}
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={recording ? "Listening…" : isNexsys ? "Ask or speak to NexSys…" : "Ask Chatty anything…"}
                className="flex-1 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50"
                data-testid="chatty-input"
              />
              <button type="submit" disabled={busy} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-500 text-black transition-opacity disabled:opacity-50" data-testid="chatty-send">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* launcher */}
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="relative ml-auto flex items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500 text-black shadow-[0_0_36px_rgba(34,211,238,0.55)]"
        style={{ height: 60, width: 60 }}
        data-testid="chatty-launcher"
        aria-label="Open Chatty"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && <span className="absolute inset-0 -z-10 rounded-full bg-cyan-400/40 blur-md nx-pulse" />}
        {(speaking || recording) && !open && (
          <span className="absolute -inset-1 -z-10 rounded-full border-2 border-cyan-300/70 nx-pulse" />
        )}
      </motion.button>
    </div>
  );
}
